/**
 * First page a visitor landed on, and the site that sent them.
 *
 * utm_* and fbclid already survive a later visit (utmParams.js, the fbclid
 * localStorage key). Organic search and AI assistants do not put those on the
 * URL, so the referrer and the landing path have to be remembered too.
 *
 * First touch wins for 30 days. A later visit does not replace it, and a
 * referrer that is us, the app, checkout, or Stripe is not a source. The
 * value is written to localStorage and to a cookie on the parent domain
 * (.withme.so, .fitwith.io, .fitwithofficial.com) so the marketing site and
 * the app can be different subdomains. Sign-up also carries ft_ref, ft_path,
 * ft_at and ft_gclid the same way fbclid already travels, for a hop the
 * cookie cannot cross.
 *
 * getFirstTouch only reads what was already stored or passed on the URL. It
 * does not invent a visit from the current page. captureFirstTouch does that,
 * and it is what pages call on load.
 */

export const FIRST_TOUCH_COOKIE = 'wm_first_touch';
export const FIRST_TOUCH_STORAGE_KEY = 'wm_first_touch';
export const FIRST_TOUCH_MAX_AGE_DAYS = 30;
export const FIRST_TOUCH_MAX_AGE_MS = FIRST_TOUCH_MAX_AGE_DAYS * 86400000;
export const FIRST_TOUCH_QUERY_KEYS = ['ft_ref', 'ft_path', 'ft_at', 'ft_gclid'];

const INTERNAL_SUFFIXES = [
   'withme.so',
   'fitwith.io',
   'fitwithofficial.com',
   'with-me.shop',
   'with-me.store',
   'stripe.com',
   'stripe.network',
];
const INTERNAL_HOSTS = new Set(['localhost', '127.0.0.1', '::1']);
const EMPTY_VALUES = new Set(['', 'null', 'undefined', 'none']);

export function normalizeHost(value) {
   let host = String(value || '').trim().toLowerCase();
   if (EMPTY_VALUES.has(host)) return '';
   if (host.includes('://')) {
      try {
         host = new URL(host).hostname || '';
      } catch (e) {
         host = '';
      }
   } else {
      host = host.split('/')[0];
      if (host.includes('@')) host = host.split('@').pop();
      if (host.startsWith('[') && host.includes(']')) {
         host = host.slice(1, host.indexOf(']'));
      } else if (host !== '::1') {
         host = host.split(':')[0];
      }
   }
   host = host.replace(/^\.+|\.+$/g, '');
   if (host.startsWith('www.')) host = host.slice(4);
   return host.slice(0, 255);
}

export function isInternalReferrer(host) {
   host = normalizeHost(host);
   if (!host) return false;
   if (INTERNAL_HOSTS.has(host)) return true;
   return INTERNAL_SUFFIXES.some((suffix) => host === suffix || host.endsWith('.' + suffix));
}

export function cookieDomain(hostname) {
   const host = normalizeHost(hostname);
   if (host === 'withme.so' || host.endsWith('.withme.so')) return '.withme.so';
   if (host === 'fitwith.io' || host.endsWith('.fitwith.io')) return '.fitwith.io';
   if (host === 'fitwithofficial.com' || host.endsWith('.fitwithofficial.com')) return '.fitwithofficial.com';
   return '';
}

export function firstTouchCookieAttributes(hostname, protocol) {
   const parts = ['Path=/', 'Max-Age=' + Math.round(FIRST_TOUCH_MAX_AGE_MS / 1000), 'SameSite=Lax'];
   const domain = cookieDomain(hostname);
   if (domain) parts.push('Domain=' + domain);
   if (protocol === 'https:') parts.push('Secure');
   return parts.join('; ');
}

function cleanPath(value) {
   let path = String(value || '').trim();
   if (EMPTY_VALUES.has(path.toLowerCase())) return '';
   if (path.includes('://')) {
      try {
         path = new URL(path).pathname || '';
      } catch (e) {
         path = '';
      }
   }
   path = path.split('?')[0].split('#')[0].trim();
   if (!path) return '';
   if (!path.startsWith('/')) path = '/' + path;
   return path.slice(0, 500);
}

function cleanId(value) {
   const text = String(value || '').trim();
   if (EMPTY_VALUES.has(text.toLowerCase())) return '';
   return text.slice(0, 1000);
}

function toSearch(search) {
   if (search && typeof search.get === 'function') return search;
   if (typeof search === 'string') return new URLSearchParams(search);
   try {
      return new URLSearchParams(window.location.search);
   } catch (e) {
      return new URLSearchParams();
   }
}

function parseTouchAt(value) {
   if (value === null || value === undefined || value === '') return null;
   const num = Number(value);
   if (!Number.isNaN(num) && Math.abs(num) > 1000000000) {
      return Math.abs(num) > 10000000000 ? num : num * 1000;
   }
   const parsed = Date.parse(String(value));
   return Number.isNaN(parsed) ? null : parsed;
}

function isFresh(at, now) {
   if (at === null || at === undefined || Number.isNaN(at)) return false;
   const age = now - at;
   if (age < -86400000) return false;
   return age <= FIRST_TOUCH_MAX_AGE_MS;
}

function asTouch(host, path, at, gclid) {
   if (isInternalReferrer(host)) host = '';
   host = host || '';
   path = path || '';
   gclid = gclid || '';
   if (!host && !path && !gclid && (at === null || at === undefined)) return null;
   return {
      referrer_host: host,
      landing_path: path,
      touch_at: at === null || at === undefined ? null : at,
      gclid,
   };
}

function fromParts(host, path, at, gclid) {
   return asTouch(normalizeHost(host), cleanPath(path), parseTouchAt(at), cleanId(gclid));
}

function fromParams(params) {
   if (!params) return null;
   return fromParts(
      params.get('ft_ref') || params.get('first_referrer_host'),
      params.get('ft_path') || params.get('first_landing_path'),
      params.get('ft_at') || params.get('first_touch_at'),
      params.get('ft_gclid'),
   );
}

function fromPayload(payload) {
   if (!payload || typeof payload !== 'object') return null;
   return fromParts(
      payload.referrer_host || payload.first_referrer_host,
      payload.landing_path || payload.first_landing_path,
      payload.touch_at !== undefined && payload.touch_at !== null && payload.touch_at !== ''
         ? payload.touch_at
         : payload.first_touch_at,
      payload.gclid || payload.ft_gclid,
   );
}

function readJson(raw) {
   if (!raw) return null;
   try {
      return JSON.parse(raw);
   } catch (e) {
      try {
         return JSON.parse(decodeURIComponent(raw));
      } catch (err) {
         return null;
      }
   }
}

function readLocal() {
   try {
      return fromPayload(readJson(window.localStorage.getItem(FIRST_TOUCH_STORAGE_KEY)));
   } catch (e) {
      return null;
   }
}

function readCookie() {
   try {
      const parts = String(document.cookie || '').split(';');
      for (let i = 0; i < parts.length; i += 1) {
         const part = parts[i].trim();
         const eq = part.indexOf('=');
         if (eq === -1) continue;
         if (part.slice(0, eq) !== FIRST_TOUCH_COOKIE) continue;
         return fromPayload(readJson(part.slice(eq + 1)));
      }
   } catch (e) {
      return null;
   }
   return null;
}

function freshTouch(touch, now) {
   if (!touch) return null;
   const at = touch.touch_at === null || touch.touch_at === undefined ? now : touch.touch_at;
   if (!isFresh(at, now)) return null;
   return { ...touch, touch_at: at };
}

function earliest(touches, now) {
   let chosen = null;
   touches.forEach((touch) => {
      const fresh = freshTouch(touch, now);
      if (!fresh) return;
      if (!chosen || fresh.touch_at < chosen.touch_at) chosen = fresh;
   });
   return chosen;
}

function payloadOf(touch) {
   return {
      referrer_host: touch.referrer_host || '',
      landing_path: touch.landing_path || '',
      touch_at: touch.touch_at,
      gclid: touch.gclid || '',
   };
}

function writeLocal(payload) {
   try {
      window.localStorage.setItem(FIRST_TOUCH_STORAGE_KEY, JSON.stringify(payload));
   } catch (e) {
      // storage blocked: the cookie and the URL params are the other copies
   }
}

function writeCookie(payload) {
   try {
      const attrs = firstTouchCookieAttributes(window.location.hostname, window.location.protocol);
      document.cookie = FIRST_TOUCH_COOKIE + '=' + encodeURIComponent(JSON.stringify(payload)) + '; ' + attrs;
   } catch (e) {
      // a blocked cookie still leaves localStorage and the ft_* query params
   }
}

function samePayload(a, b) {
   if (!a || !b) return false;
   return a.referrer_host === b.referrer_host
      && a.landing_path === b.landing_path
      && a.touch_at === b.touch_at
      && a.gclid === b.gclid;
}

function persistIfNeeded(touch) {
   const payload = payloadOf(touch);
   const local = readLocal();
   const cookie = readCookie();
   if (!samePayload(local, payload)) writeLocal(payload);
   if (!samePayload(cookie, payload)) writeCookie(payload);
}

/**
 * The referrer and path of this page load. An internal referrer is blank, so
 * it cannot become the source. Exported for tests.
 */
export function visitTouch({ referrer, path, search, now }) {
   const params = new URLSearchParams(search || '');
   return asTouch(
      normalizeHost(referrer),
      cleanPath(path),
      now,
      cleanId(params.get('gclid')),
   );
}

/**
 * Stored first touch, or the ft_* values on `search` when that visit is older.
 * Does not record the current page.
 *
 * @param {URLSearchParams|string} [search] omitted: the current URL. Pass '' to
 *   ignore the URL and read storage only.
 */
export function getFirstTouch(search) {
   const now = Date.now();
   return earliest([readLocal(), readCookie(), fromParams(toSearch(search))], now);
}

/**
 * Remember this visit when nothing fresh is stored. A stored touch is left
 * as it is, including its time, and is copied onto the cookie when the cookie
 * is missing so the other subdomain can read it.
 */
export function captureFirstTouch() {
   try {
      const now = Date.now();
      const existing = earliest([readLocal(), readCookie(), fromParams(toSearch())], now);
      if (existing) {
         persistIfNeeded(existing);
         return existing;
      }
      const visit = visitTouch({
         referrer: typeof document !== 'undefined' ? document.referrer : '',
         path: window.location.pathname,
         search: window.location.search,
         now,
      });
      if (!visit) return null;
      persistIfNeeded({ ...visit, touch_at: now });
      return { ...visit, touch_at: now };
   } catch (e) {
      return null;
   }
}

export function clearFirstTouch() {
   try {
      window.localStorage.removeItem(FIRST_TOUCH_STORAGE_KEY);
   } catch (e) {
      // nothing to do
   }
   try {
      document.cookie = FIRST_TOUCH_COOKIE + '=; Path=/; Max-Age=0';
      const domain = cookieDomain(window.location.hostname);
      if (domain) {
         document.cookie = FIRST_TOUCH_COOKIE + '=; Path=/; Max-Age=0; Domain=' + domain;
      }
   } catch (e) {
      // nothing to do
   }
}

function queryFromTouch(touch) {
   if (!touch) return '';
   const parts = [];
   if (touch.referrer_host) parts.push('ft_ref=' + encodeURIComponent(touch.referrer_host));
   if (touch.landing_path) parts.push('ft_path=' + encodeURIComponent(touch.landing_path));
   if (touch.touch_at) parts.push('ft_at=' + encodeURIComponent(String(touch.touch_at)));
   if (touch.gclid) parts.push('ft_gclid=' + encodeURIComponent(touch.gclid));
   return parts.join('&');
}

/** ft_* query string with no leading ? or &. Empty when nothing is stored. */
export function firstTouchQuery(search) {
   return queryFromTouch(getFirstTouch(search));
}

/**
 * The ?fbclid=&ft_* suffix already used for Google sign-up, plus the first
 * touch. Records the current visit first so a click on the landing page still
 * carries it. Empty string when there is nothing to add.
 */
export function signupAttributionQuery(search) {
   captureFirstTouch();
   let fbclid = '';
   try {
      fbclid = toSearch(search).get('fbclid') || '';
   } catch (e) {
      fbclid = '';
   }
   if (!fbclid) {
      try {
         fbclid = window.localStorage.getItem('fbclid') || '';
      } catch (e) {
         fbclid = '';
      }
   }
   fbclid = cleanId(fbclid);
   const parts = [];
   if (fbclid) parts.push('fbclid=' + encodeURIComponent(fbclid));
   const ft = firstTouchQuery(search);
   if (ft) parts.push(ft);
   return parts.length ? '?' + parts.join('&') : '';
}

/** Add the stored first touch to a sign-up form. No-op when nothing is stored. */
export function appendFirstTouch(formData, search) {
   const touch = getFirstTouch(search);
   if (!touch || !formData || typeof formData.append !== 'function') return formData;
   const add = (name, value) => {
      if (!value) return;
      if (typeof formData.has === 'function' && formData.has(name)) return;
      formData.append(name, String(value));
   };
   add('ft_ref', touch.referrer_host);
   add('ft_path', touch.landing_path);
   add('ft_at', touch.touch_at);
   add('ft_gclid', touch.gclid);
   return formData;
}

/** `href` with the stored ft_* params added. Params already on href stay. */
export function withFirstTouch(href) {
   if (typeof href !== 'string' || typeof window === 'undefined') return href;
   const touch = getFirstTouch();
   if (!touch) return href;
   try {
      const url = new URL(href, window.location.origin);
      const set = (key, value) => {
         if (!value || url.searchParams.has(key)) return;
         url.searchParams.set(key, String(value));
      };
      set('ft_ref', touch.referrer_host);
      set('ft_path', touch.landing_path);
      set('ft_at', touch.touch_at);
      set('ft_gclid', touch.gclid);
      if (href.startsWith('http://') || href.startsWith('https://')) return url.toString();
      return url.pathname + url.search + url.hash;
   } catch (e) {
      return href;
   }
}

export default getFirstTouch;
